/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';

import { DocumentContact } from 'common/types';
import { initContact } from 'admin/utils/contact-utils/contact-utils';
import { ORDER_RETURN_CONTACT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    ContactListState,
    createContactDraftReducers,
    createContactListReducers,
} from 'admin/stores/redux/contactSliceHelpers/contactSliceHelpers';

export type State = ContactListState;

const newDraftContact = () =>
    initContact(ORDER_RETURN_CONTACT_TYPE.CT_RETURN_CONTACT);

interface StateSlice {
    adminOrderReturnCreation: State;
}

const CONTACT_PAYLOAD_KEYS = [
    'typeId',
    'name',
    'email',
    'mobile',
    'mobileCountry',
    'phone',
    'phoneCountry',
    'phoneExt',
] as const;

export type OrderReturnContactPayload = Pick<
    DocumentContact,
    (typeof CONTACT_PAYLOAD_KEYS)[number]
>;

const initialState: State = {
    contacts: [],
    draftContact: newDraftContact(),
};

export const adminOrderReturnCreation = createSlice({
    name: 'adminOrderReturnCreation',
    initialState,
    reducers: {
        ...createContactListReducers<State>(),
        ...createContactDraftReducers<State>(newDraftContact),

        reset: () => ({ ...initialState, draftContact: newDraftContact() }),
    },
});

export const {
    addContact,
    removeContact,
    setDraftContact,
    resetDraftContact,
    reset,
} = adminOrderReturnCreation.actions;

export const orderReturnSelectedContacts = (state: StateSlice) =>
    state.adminOrderReturnCreation.contacts;

export const orderReturnDraftContact = (state: StateSlice) =>
    state.adminOrderReturnCreation.draftContact;

export const orderReturnSelectedContactsPayload = createSelector(
    [orderReturnSelectedContacts],
    (contacts): OrderReturnContactPayload[] =>
        contacts.map(
            contact =>
                Object.fromEntries(
                    CONTACT_PAYLOAD_KEYS.map(key => [key, contact[key]])
                ) as OrderReturnContactPayload
        )
);

export default adminOrderReturnCreation.reducer;
